import axios from 'axios';

// REACT_APP_E2CARE_API takes priority when set (points at real e2-care backend).
// Falls back to REACT_APP_BACKEND_URL (the platform-managed preview URL used by our local sim).
const BACKEND_URL =
  process.env.REACT_APP_E2CARE_API || process.env.REACT_APP_BACKEND_URL;

export const API = `${BACKEND_URL}/api`;

// When the API is served through an ngrok tunnel (dev preview), send the
// skip-warning header so ngrok returns JSON instead of its HTML interstitial.
// Only applied for ngrok hosts, so prod/other backends are unaffected.
const IS_NGROK = /ngrok(-free)?\.(dev|app|io)/.test(BACKEND_URL || '');

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    ...(IS_NGROK ? { 'ngrok-skip-browser-warning': 'true' } : {}),
  },
});

// ---------- Bearer token auth ----------
// The backend returns access_token on /auth/login and accepts it as
// `Authorization: Bearer <token>`. We store it so auth survives page reloads
// and works cross-domain (dev preview -> ngrok), where third-party session
// cookies are blocked by the browser. Cookies (withCredentials) still work for
// same-domain prod as a fallback.
const TOKEN_STORAGE_KEY = 'e2care.access_token';
const AUTH_EXEMPT_PATHS = ['/auth/login', '/auth/register'];

export function setAuthToken(token) {
  if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
}
export function clearAuthToken() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}
export function getAuthToken() {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

api.interceptors.request.use((config) => {
  const url = config.url || '';
  const isAuthExempt = AUTH_EXEMPT_PATHS.some((p) => url === p || url.startsWith(`${p}?`));
  if (!isAuthExempt) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return config;
});

// ---------- Multi-tenant X-Org-Id header ----------
// The OrgContext persists the active org's id to localStorage under this key.
// Reading from storage (instead of a closure) means the header always reflects
// the latest selection without us having to plumb the value through every call.
const ORG_STORAGE_KEY = 'e2care.current_org';
const ORG_PUBLIC_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/health',
  '/me/organizations',   // listing orgs is how we BOOTSTRAP the header
];

function isPublicPath(url = '') {
  // axios passes URLs as relative to baseURL — e.g. '/patients', '/auth/login'.
  // Match against the start of the path so '/auth/login' and any sub-route are covered.
  return ORG_PUBLIC_PATHS.some((p) => url === p || url.startsWith(`${p}?`) || url.startsWith(`${p}/`));
}

api.interceptors.request.use((config) => {
  if (!isPublicPath(config.url)) {
    config.headers = config.headers || {};
    // Respect an explicit per-request X-Org-Id override (used for cross-org
    // admin reads/writes in the "Full list" view). Only fall back to the active
    // org persisted by OrgContext when no override was supplied.
    // `skipOrg: true` = cross-org super-admin call: send no X-Org-Id at all.
    if (config.skipOrg) {
      delete config.headers['X-Org-Id'];
    } else if (config.headers['X-Org-Id'] == null) {
      const orgId = localStorage.getItem(ORG_STORAGE_KEY);
      if (orgId) config.headers['X-Org-Id'] = orgId;
    }
  }
  return config;
});

// ---------- Notification language (Telegram + push) ----------
// GET /api/languages → { languages: [...] }. Returns just the array.
export const getNotificationLanguages = () =>
  api.get('/languages').then((r) => (Array.isArray(r.data?.languages) ? r.data.languages : []));

// PUT /api/users/me/language { language } → { language }.
export const setMyNotificationLanguage = (language) =>
  api.put('/users/me/language', { language }).then((r) => r.data);

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
